import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { toastBlue } from '@/utils/toast';
import { Eye, EyeOff, Mail, Lock, User, Phone, CreditCard, ArrowLeft, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AuthPage() {
  const [mode, setMode] = useState('login'); // login, register, forgot, reset
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/';
  
  // Form data
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    cpf: '',
    phone: '',
    confirmPassword: '',
    newPassword: ''
  });
  
  const handleChange = (e) => {
    let { name, value } = e.target;
    
    // Format CPF
    if (name === 'cpf') {
      value = value.replace(/\D/g, '').slice(0, 11);
      if (value.length > 9) {
        value = value.replace(/(\d{3})(\d{3})(\d{3})(\d{1,2})/, '$1.$2.$3-$4');
      } else if (value.length > 6) {
        value = value.replace(/(\d{3})(\d{3})(\d{1,3})/, '$1.$2.$3');
      } else if (value.length > 3) {
        value = value.replace(/(\d{3})(\d{1,3})/, '$1.$2');
      }
    }
    
    // Format phone
    if (name === 'phone') {
      value = value.replace(/\D/g, '').slice(0, 11);
      if (value.length > 6) {
        value = value.replace(/(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3');
      } else if (value.length > 2) {
        value = value.replace(/(\d{2})(\d{0,5})/, '($1) $2');
      }
    }
    
    setFormData(prev => ({ ...prev, [name]: value }));
    // Limpa erro do campo ao digitar
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: null }));
    }
  };
  
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const { data } = await axios.post(`${API}/api/auth/login`, {
        email: formData.email,
        password: formData.password
      });
      
      login(data.user, data.access_token);
      toast.success('Login realizado com sucesso!');
      navigate(from, { replace: true });
      
    } catch (error) {
      const msg = error.response?.data?.detail || 'Email ou senha incorretos';
      toastBlue(msg);
    } finally {
      setLoading(false);
    }
  };
  
  const validateRegisterForm = () => {
    const errors = {};

    if (!formData.name.trim() || formData.name.trim().length < 3)
      errors.name = 'Nome deve ter pelo menos 3 caracteres';

    if (!formData.email || !/^[\w.\-+]+@[\w\.-]+\.\w{2,}$/.test(formData.email))
      errors.email = 'Digite um email válido';

    const cpfClean = formData.cpf.replace(/\D/g, '');
    if (cpfClean.length !== 11) {
      errors.cpf = 'CPF deve ter 11 dígitos';
    } else if (cpfClean === cpfClean[0].repeat(11)) {
      errors.cpf = 'CPF inválido';
    } else {
      const calc = (cpf, len) => {
        let sum = 0;
        for (let i = 0; i < len; i++) sum += parseInt(cpf[i]) * (len + 1 - i);
        const r = (sum * 10) % 11;
        return r === 10 ? 0 : r;
      };
      if (calc(cpfClean, 9) !== parseInt(cpfClean[9]) || calc(cpfClean, 10) !== parseInt(cpfClean[10]))
        errors.cpf = 'CPF inválido';
    }

    const phoneClean = formData.phone.replace(/\D/g, '');
    if (phoneClean.length < 10)
      errors.phone = 'Telefone inválido (mínimo 10 dígitos)';

    if (formData.password.length < 6)
      errors.password = 'Senha deve ter pelo menos 6 caracteres';

    if (formData.password !== formData.confirmPassword)
      errors.confirmPassword = 'As senhas não conferem';

    return errors;
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    const frontendErrors = validateRegisterForm();
    if (Object.keys(frontendErrors).length > 0) {
      setFieldErrors(frontendErrors);
      return;
    }

    setFieldErrors({});
    setLoading(true);

    try {
      const { data } = await axios.post(`${API}/api/auth/register`, {
        email: formData.email,
        password: formData.password,
        name: formData.name,
        cpf: formData.cpf,
        phone: formData.phone
      });

      login(data.user, data.access_token);
      toast.success('Conta criada com sucesso!');
      navigate(from, { replace: true });

    } catch (error) {
      let errorMsg = '';
      const detail = error.response?.data?.detail;

      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail)) {
        const firstErr = detail[0];
        const fieldName = firstErr?.loc?.[firstErr.loc.length - 1] || '';
        const names = { cpf: 'CPF', phone: 'Telefone', email: 'Email', name: 'Nome', password: 'Senha' };
        errorMsg = `${names[fieldName] || fieldName} é obrigatório`;
      } else {
        errorMsg = 'Erro ao cadastrar. Tente novamente.';
      }

      toastBlue(errorMsg);

      if (errorMsg.toLowerCase().includes('cpf')) setFieldErrors({ cpf: errorMsg });
      else if (errorMsg.toLowerCase().includes('email')) setFieldErrors({ email: errorMsg });
      else if (errorMsg.toLowerCase().includes('telefone')) setFieldErrors({ phone: errorMsg });
    } finally {
      setLoading(false);
    }
  };
  
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/api/auth/forgot-password`, {
        email: formData.email
      });
      
      setResetEmail(formData.email);
      setMode('reset');
      toast.success('Código enviado para seu email!');
      
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erro ao solicitar recuperação';
      toastBlue(msg);
    } finally {
      setLoading(false);
    }
  };
  
  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (formData.newPassword.length < 6) {
      toastBlue('A senha deve ter pelo menos 6 caracteres');
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.post(`${API}/api/auth/reset-password`, {
        email: resetEmail,
        code: resetCode,
        new_password: formData.newPassword
      });
      
      toast.success('Senha alterada com sucesso!');
      setMode('login');
      setFormData(prev => ({ ...prev, email: resetEmail, password: '' }));
      
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erro ao redefinir senha';
      toastBlue(msg);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-[#f7f7f7] flex items-center justify-center px-4 py-12" data-testid="auth-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center mb-8">
          <picture>
            <source srcSet="/logo-new-sm.webp" type="image/webp" />
            <img src="/logo-new-sm.png" alt="Outlet Premium" className="h-12 w-auto" loading="eager" />
          </picture>
          <div className="flex flex-col ml-2">
            <span className="text-[#0a0a0a] font-semibold text-2xl" style={{ fontFamily: "'Playfair Display', serif" }}>
              OUTLET
            </span>
            <span className="font-medium text-[10px] tracking-[0.35em] uppercase -mt-1" style={{ color: '#787674' }}>
              PREMIUM
            </span>
          </div>
        </Link>
        
        {/* Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
          
          {/* Login Form */}
          {mode === 'login' && (
            <>
              <h1 className="text-2xl font-semibold text-gray-900 text-center mb-6">
                Entrar na conta
              </h1>
              
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="seu@email.com"
                      className="pl-10 h-12"
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Senha</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="••••••••"
                      className="pl-10 pr-10 h-12"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
                
                <div className="flex justify-end pt-1">
                  <button
                    type="button"
                    onClick={() => setMode('forgot')}
                    className="text-[13px] font-medium text-gray-500 hover:text-[#C41E3A] tracking-wide transition-colors"
                    style={{ fontFamily: "'Montserrat', 'DM Sans', sans-serif" }}
                  >
                    Esqueci minha senha
                  </button>
                </div>
                
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="login-submit-btn"
                  className="w-full bg-[#0a0a0a] hover:bg-[#1a1a1a] text-white h-12"
                >
                  {loading ? 'Entrando...' : 'Entrar'}
                </Button>
              </form>
              
              <Button
                type="button"
                onClick={() => setMode('register')}
                data-testid="goto-register-btn"
                className="relative overflow-hidden w-full mt-3 bg-[#0a0a0a] hover:bg-[#1a1a1a] text-white border-0 h-12 font-medium rounded-md group"
              >
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 overflow-hidden rounded-md"
                >
                  <span
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '50%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.10) 30%, rgba(255,255,255,0.35) 50%, rgba(255,255,255,0.10) 70%, transparent 100%)',
                      animation: 'shimmer-slide 2.8s ease-in-out infinite',
                    }}
                  />
                </span>
                <span className="relative z-[1]">Criar conta</span>
              </Button>
              <p className="mt-2 text-center text-xs text-gray-500">
                Leva apenas 45 segundos ⚡
              </p>

            </>
          )}
          
          {/* Register Form */}
          {mode === 'register' && (
            <>
              <button
                onClick={() => setMode('login')}
                className="flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Voltar
              </button>
              
              <h1 className="text-2xl font-semibold text-gray-900 text-center mb-6">
                Criar conta
              </h1>
              
              <form onSubmit={handleRegister} noValidate className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome completo</label>
                  <div className="relative">
                    <User className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${fieldErrors.name ? 'text-blue-500' : 'text-gray-400'}`} />
                    <Input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Seu nome completo"
                      className={`pl-10 h-12 ${fieldErrors.name ? 'border-blue-500 focus-visible:ring-blue-400' : ''}`}
                      required
                    />
                  </div>
                  {fieldErrors.name && <p className="text-blue-600 text-xs mt-1 flex items-center gap-1"><span>&#9679;</span>{fieldErrors.name}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <div className="relative">
                    <Mail className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${fieldErrors.email ? 'text-blue-500' : 'text-gray-400'}`} />
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="seu@email.com"
                      className={`pl-10 h-12 ${fieldErrors.email ? 'border-blue-500 focus-visible:ring-blue-400' : ''}`}
                      required
                    />
                  </div>
                  {fieldErrors.email && <p className="text-blue-600 text-xs mt-1 flex items-center gap-1"><span>&#9679;</span>{fieldErrors.email}</p>}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">CPF</label>
                    <div className="relative">
                      <CreditCard className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${fieldErrors.cpf ? 'text-blue-500' : 'text-gray-400'}`} />
                      <Input
                        type="text"
                        name="cpf"
                        value={formData.cpf}
                        onChange={handleChange}
                        placeholder="000.000.000-00"
                        className={`pl-10 h-12 ${fieldErrors.cpf ? 'border-blue-500 focus-visible:ring-blue-400' : ''}`}
                        required
                      />
                    </div>
                    {fieldErrors.cpf && <p className="text-blue-600 text-xs mt-1 flex items-center gap-1"><span>&#9679;</span>{fieldErrors.cpf}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
                    <div className="relative">
                      <Phone className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${fieldErrors.phone ? 'text-blue-500' : 'text-gray-400'}`} />
                      <Input
                        type="text"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="(00) 00000-0000"
                        className={`pl-10 h-12 ${fieldErrors.phone ? 'border-blue-500 focus-visible:ring-blue-400' : ''}`}
                        required
                      />
                    </div>
                    {fieldErrors.phone && <p className="text-blue-600 text-xs mt-1 flex items-center gap-1"><span>&#9679;</span>{fieldErrors.phone}</p>}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Senha</label>
                  <div className="relative">
                    <Lock className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${fieldErrors.password ? 'text-blue-500' : 'text-gray-400'}`} />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Mínimo 6 caracteres"
                      className={`pl-10 pr-10 h-12 ${fieldErrors.password ? 'border-blue-500 focus-visible:ring-blue-400' : ''}`}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  {fieldErrors.password && <p className="text-blue-600 text-xs mt-1 flex items-center gap-1"><span>&#9679;</span>{fieldErrors.password}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confirmar senha</label>
                  <div className="relative">
                    <Lock className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${fieldErrors.confirmPassword ? 'text-blue-500' : 'text-gray-400'}`} />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="Repita a senha"
                      className={`pl-10 h-12 ${fieldErrors.confirmPassword ? 'border-blue-500 focus-visible:ring-blue-400' : ''}`}
                      required
                    />
                  </div>
                  {fieldErrors.confirmPassword && <p className="text-blue-600 text-xs mt-1 flex items-center gap-1"><span>&#9679;</span>{fieldErrors.confirmPassword}</p>}
                </div>
                
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="register-submit-btn"
                  className="w-full bg-[#C41E3A] hover:bg-[#a01830] text-white h-12"
                >
                  {loading ? 'Criando conta...' : 'Criar conta'}
                </Button>
              </form>
              
              <p className="mt-6 text-center text-sm text-gray-600">
                Já tem conta?{' '}
                <button
                  onClick={() => setMode('login')}
                  className="text-[#C41E3A] font-medium hover:underline"
                >
                  Entrar
                </button>
              </p>
            </>
          )}
          
          {/* Forgot Password Form */}
          {mode === 'forgot' && (
            <>
              <button
                onClick={() => setMode('login')}
                className="flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Voltar
              </button>
              
              <h1 className="text-2xl font-semibold text-gray-900 text-center mb-2">
                Recuperar senha
              </h1>
              <p className="text-gray-600 text-center text-sm mb-6">
                Digite seu email para receber o código de recuperação
              </p>
              
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="seu@email.com"
                      className="pl-10 h-12"
                      required
                    />
                  </div>
                </div>
                
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#0a0a0a] hover:bg-[#1a1a1a] text-white h-12"
                >
                  {loading ? 'Enviando...' : 'Enviar código'}
                </Button>
              </form>
            </>
          )}
          
          {/* Reset Password Form */}
          {mode === 'reset' && (
            <>
              <div className="flex items-center justify-center w-12 h-12 bg-green-100 rounded-full mx-auto mb-4">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              
              <h1 className="text-2xl font-semibold text-gray-900 text-center mb-2">
                Código enviado!
              </h1>
              <p className="text-gray-600 text-center text-sm mb-6">
                Digite o código de 6 dígitos enviado para<br />
                <span className="font-medium text-gray-900">{resetEmail}</span>
              </p>
              
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Código</label>
                  <Input
                    type="text"
                    value={resetCode}
                    onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className="h-12 text-center text-2xl tracking-[0.5em] font-mono"
                    maxLength={6}
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nova senha</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      name="newPassword"
                      value={formData.newPassword}
                      onChange={handleChange}
                      placeholder="Mínimo 6 caracteres"
                      className="pl-10 pr-10 h-12"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
                
                <Button
                  type="submit"
                  disabled={loading || resetCode.length !== 6}
                  className="w-full bg-[#C41E3A] hover:bg-[#a01830] text-white h-12"
                >
                  {loading ? 'Alterando...' : 'Alterar senha'}
                </Button>
              </form>
              
              <button
                onClick={() => setMode('forgot')}
                className="mt-4 text-sm text-gray-600 hover:text-gray-900 w-full text-center"
              >
                Não recebi o código
              </button>
            </>
          )}
        </div>
        
        {/* Footer */}
        <p className="mt-6 text-center text-xs text-gray-500">
          Ao continuar, você concorda com nossos{' '}
          <Link to="/termos" className="underline">Termos de Uso</Link>
          {' '}e{' '}
          <Link to="/privacidade" className="underline">Política de Privacidade</Link>
        </p>
      </div>
    </div>
  );
}
